import { BaseSyntheticEvent, useCallback, useMemo } from "react";

import { FormProvider, useForm } from "react-hook-form";

import { FCWithChildren } from "@/jenesys-types/components";

import { vestResolver, VestSchema } from "./vestResolver";

// Helper to sanitize form data, ensuring empty string numeric fields are converted to null in the case where a failed
// Document is saved with new- empty string values.
const sanitizeFormData = (data: any): any => {
  if (!data) return data;

  if (typeof data === "object" && data !== null) {
    if (Array.isArray(data)) {
      return data.map(sanitizeFormData);
    }

    const result = { ...data };
    Object.keys(result).forEach((key) => {
      if (typeof result[key] === "object" && result[key] !== null) {
        result[key] = sanitizeFormData(result[key]);
      } else if (key === "vat_exclusive" && result[key] === null) {
        result[key] = false;
      } else if (
        result[key] === "" &&
        [
          "quantity",
          "unit_price",
          "total",
          "vat",
          "tax_code",
          "discount_total",
          "service_charge",
          "delivery_charge"
        ].includes(key)
      ) {
        result[key] = null;
      }
    });
    return result;
  }

  return data;
};

export interface FormProps {
  onSubmit: (data: any) => void;
  initialValues: Record<string, any>;
  validationSchema?: VestSchema;
  extraValidationData?: Record<string, any>;
  mode?: "onSubmit" | "onChange" | "onBlur" | "all";
}

export const Form: FCWithChildren<FormProps> = ({
  onSubmit,
  initialValues,
  validationSchema,
  extraValidationData,
  mode = "onSubmit",
  children
}) => {
  const formContext = useMemo(
    () => ({
      initialValues,
      extraData: extraValidationData
    }),
    [initialValues, extraValidationData]
  );

  // Remove the event so onSubmit is only called with the data.
  const onSubmitWrapper = useCallback(
    (data: any, _event?: BaseSyntheticEvent) => {
      // Sanitize the form data before submission
      const sanitizedData = sanitizeFormData(data);
      onSubmit(sanitizedData);
    },
    [onSubmit]
  );

  const form = useForm({
    defaultValues: initialValues,
    resolver: validationSchema ? vestResolver(validationSchema) : undefined,
    context: formContext,
    mode
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmitWrapper)} noValidate>
        {children}
      </form>
    </FormProvider>
  );
};
